// 관리 API 호출 래퍼 — 인증 헤더 부착·자동 재발급·표준 에러 처리
// 에러코드 정의: docs/PROJECT.md §5.3
import { clearToken, getCsrfToken, getToken, setToken } from "./tokenStorage.ts";

export class ApiError extends Error {
  constructor(
    readonly code: string,
    override readonly message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** 약관 미동의 — 동의 화면으로 유도해야 한다 */
  get needsTerms(): boolean {
    return this.code === "TERMS_AGREEMENT_REQUIRED";
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** 인증이 필요 없는 공개 엔드포인트 */
  anonymous?: boolean;
  /** 추가 헤더 — 쿠키 인증 경로에 CSRF 토큰을 실을 때 쓴다 */
  headers?: Record<string, string>;
}

/**
 * 진행 중인 재발급 요청. 401이 동시에 여러 개 터져도 재발급은 **한 번만** 한다 —
 * 리프레시 토큰은 쓸 때마다 회전하므로, 병렬로 두 번 보내면 두 번째가
 * '이미 쓴 토큰'으로 보여 서버가 세션 전체를 끊어 버린다(재사용 감지).
 */
let refreshing: Promise<boolean> | null = null;

/**
 * 재발급. 리프레시 토큰은 **httpOnly 쿠키**라 JS가 읽지도 보내지도 않는다 —
 * 브라우저가 자동으로 싣는다(same-origin이므로 credentials 기본값으로 충분).
 * 대신 CSRF 토큰을 헤더로 되돌려 보낸다(double-submit).
 */
async function refreshAccessToken(): Promise<boolean> {
  const response = await fetch("/api/v1/auth/refresh", {
    method: "POST",
    headers: csrfHeaders(),
  });

  if (!response.ok) {
    clearToken(); // 재발급이 거부되면 세션이 끝난 것이다
    return false;
  }

  const payload = (await response.json()) as { accessToken: string };
  setToken(payload.accessToken);
  return true;
}

export function csrfHeaders(): Record<string, string> {
  const token = getCsrfToken();
  return token ? { "X-XSRF-TOKEN": token } : {};
}

function ensureRefreshing(): Promise<boolean> {
  refreshing ??= refreshAccessToken()
    .catch(() => {
      clearToken();
      return false;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const { method = "GET", body, anonymous = false, headers = {} } = options;
  const token = anonymous ? null : getToken();

  return fetch(`/api/v1${path}`, {
    method,
    headers: {
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await send(path, options);

  // 액세스 토큰이 만료됐을 뿐이면 조용히 재발급하고 한 번만 다시 시도한다.
  // (액세스 토큰 수명이 30분이라 정상 사용 중에도 주기적으로 여기를 지난다)
  if (response.status === 401 && !options.anonymous) {
    if (await ensureRefreshing()) {
      response = await send(path, options);
    }
    // 재시도까지 401이면 세션이 정말 끝난 것 — 무한 재시도를 막기 위해 정리한다
    if (response.status === 401) clearToken();
  }

  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as {
      code?: string;
      message?: string;
    };
    throw new ApiError(
      payload.code ?? "UNKNOWN",
      payload.message ?? "요청을 처리하지 못했습니다.",
      response.status,
    );
  }

  // ⚠️ 204 만 보면 안 된다. **본문 없는 200** 도 정상이다(서버가 값을 돌려줄 게 없을 때).
  //    그때 response.json() 은 예외를 던지고, 호출부는 그것을 '실패'로 표시한다 —
  //    서버는 성공했는데 화면만 실패라 운영자가 재시도하게 된다(실측: 계정 정지가 그랬다).
  if (response.status === 204 || response.headers.get("Content-Length") === "0") {
    return undefined as T;
  }
  const body = await response.text();
  return (body ? JSON.parse(body) : undefined) as T;
}
